import { Route, useLocation, Routes, useParams } from 'react-router-dom';
import { lazy, Suspense, useEffect } from 'react';
import LoadingPage from './components/global/LoadingPage';
import StoreRouteFallback from './components/global/StoreRouteFallback';
import NotFound from './pages/NotFound';
import JoinNetwork from './pages/visitor/JoinNetwork';
// Eager layout: avoids root Suspense unmounting the store shell when child pages lazy-load
import LayoutStore from './layouts/LayoutStore';

const LayoutLanding = lazy(() => import('./layouts/LayoutLanding'));

const Home = lazy(() => import('./pages/visitor/Home'));
const Contacts = lazy(() => import('./pages/visitor/Contacts'));
const Links = lazy(() => import('./pages/visitor/Links'));
const Menu = lazy(() => import('./pages/visitor/Menu'));
const Offers = lazy(() => import('./pages/visitor/Offers'));
const Favorites = lazy(() => import('./pages/visitor/Favorites'));
const Numbers = lazy(() => import('./pages/visitor/Numbers'));
const WhatsApp = lazy(() => import('./pages/visitor/WhatsApp'));
const WorkingHours = lazy(() => import('./pages/visitor/WorkingHours'));
const Product = lazy(() => import('./pages/visitor/Product'));
const Offer = lazy(() => import('./pages/visitor/Offer'));
const ShoppingCart = lazy(() => import('./pages/visitor/ShoppingCart'));
const Messaging = lazy(() => import('./pages/visitor/Messaging'));
const Reviews = lazy(() => import('./pages/visitor/Reviews'));
const AddReviews = lazy(() => import('./pages/visitor/AddReviews'));
const Shops = lazy(() => import('./pages/visitor/Shops'));
const Shop = lazy(() => import('./pages/visitor/Shop'));

const Landing = lazy(() => import('./pages/Landing'));
const GetMony = lazy(() => import('./pages/GetMony'));
const BrandNetwork = lazy(() => import('./pages/BrandNetwork'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfUse = lazy(() => import('./pages/TermsOfUse'));
const IosInstallGuide = lazy(() => import('./components/global/IosInstallGuide'));

const ScrollToTop = () => {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);
    return null;
};

function LayoutStoreWrapper() {
    const { slug } = useParams();
    return <LayoutStore key={slug} />;
}

/** Root Suspense: store URLs stay on HTML splash; landing keeps Faster Link loader. */
function RootSuspenseFallback() {
    const { pathname } = useLocation();
    const first = pathname.split('/').filter(Boolean)[0];
    const reserved = new Set([
        'join-network',
        'get-mony',
        'brand-network',
        'privacy-policy',
        'terms-of-use',
    ]);
    const isStore = first && !reserved.has(first) && !first.includes('.');
    if (isStore) return <StoreRouteFallback />;
    return <LoadingPage />;
}

const RoutesPath = () => {
    const location = useLocation();
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const refCode = queryParams.get('r');
        if (refCode) {
            localStorage.setItem('referral_code', refCode);
        }
    }, [location.search]);
    useEffect(() => {
        if (location.state?.scrollTo) {
            const id = location.state.scrollTo;
            const el = document.getElementById(id);
            if (el) {
                const offset = 100;
                const top = el.getBoundingClientRect().top + window.pageYOffset - offset;
                window.scrollTo({ top, behavior: 'smooth' });
            }
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);
    return (
        <>
            <ScrollToTop />
            <Suspense fallback={<RootSuspenseFallback />}>
                <Routes>
                    <Route element={<LayoutLanding />}>
                        <Route index element={<Landing />} />
                        <Route path="get-mony" element={<GetMony />} />
                        <Route path="brand-network" element={<BrandNetwork />} />
                        <Route path="privacy-policy" element={<PrivacyPolicy />} />
                        <Route path="terms-of-use" element={<TermsOfUse />} />
                    </Route>
                    <Route path="join-network" element={<JoinNetwork />} />
                    <Route path="/:slug/*" element={<LayoutStoreWrapper />}>
                        <Route index element={<Home />} />
                        <Route path="contact" element={<Contacts />} />
                        <Route path="phone-numbers" element={<Numbers />} />
                        <Route path="whatsApp-numbers" element={<WhatsApp />} />
                        <Route path="working-hours" element={<WorkingHours />} />
                        <Route path="menu" element={<Menu />} />
                        <Route path="menu/product/:product" element={<Product />} />
                        <Route path="shops" element={<Shops />} />
                        <Route path="shops/shop/:shop" element={<Shop />} />
                        <Route path="links" element={<Links />} />
                        <Route path="offers" element={<Offers />} />
                        <Route path="offers/offer/:offer" element={<Offer />} />
                        <Route path="favorite" element={<Favorites />} />
                        <Route path="cart" element={<ShoppingCart />} />
                        <Route path="correspondence" element={<Messaging />} />
                        <Route path="reviews" element={<Reviews />} />
                        <Route path="add-review" element={<AddReviews />} />
                        <Route path="ios-install-guide" element={<IosInstallGuide />} />
                        <Route path="*" element={<NotFound />} />
                    </Route>
                    <Route path="*" element={<NotFound />} />
                </Routes>
            </Suspense>
        </>
    );
};

export default RoutesPath;
